import styled, { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
//
import { BodyText, Button, Input, Subtitle } from '../../../../components/ui';
import { setShowLoading } from '../../../../redux/systemInfo';
import { checkSOExists } from '../../../../api/services/orderService';
import PaymentItemView from './PaymentItemView';
import { useEffect, useState } from 'react';
import PaymentInputView from './PaymentInputView';
import {
  getPaymentMethodList,
  getAddressList,
  getPaymentTerm,
} from '../../../../api/services/paymentService';
import AddAddressModal from './AddAddressModal';
import AddPaymentMethodModal from './AddPaymentMethodModal';

const PAYMENT_TERM_OPTION_ID = 'payment-term';

const MainContainer = styled.div`
  display: flex;
  margin-top: 0;
  width: 100%;
  max-width: 1450px;
  justify-content: space-between;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 1rem;
    margin-top: 0;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    gap: 0.5rem;
    margin-top: 0;
    padding: 0 1.5rem;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 2.5;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  max-width: 1600px;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    gap: 0.8rem;
    max-width: none;
    overflow: visible;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex: 0.6;
  min-height: 45vh;
  max-height: 65vh;
  min-width: 0;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.subBackground};
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: auto;
    max-height: none;
    order: -1;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const InputContainer = styled.div`
  width: 50%;
  max-width: 100%;
  border: 1px solid #ccc;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CartListConatiner = styled.div`
  display: flex;
  padding: 0.5rem;
  gap: 0.5rem;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.3rem;
    gap: 0.3rem;
  }

  @media (max-width: 480px) {
    padding: 0.2rem;
    gap: 0.2rem;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const TotalBox = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  border-radius: 10px;
  background: ${(props) => props.theme.colors.subBackground};
  padding: 10% 20px;
  font-size: 12px;
  font-weight: bold;
  text-align: right;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 15px;
    font-size: 11px;
  }

  @media (max-width: 480px) {
    gap: 15px;
    padding: 10px;
    font-size: 10px;
  }
`;

const PaymentView = ({
  cartList,
  setCurrentStep,
  poNo,
  setPoNo,
  totalTaxPrice,
  totalShippingPrice,
  totalPrice,
  setShipToInfo,
  setPaymentInfo,
  setBillToInfo,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const [shipToAddressList, setShipToAddressList] = useState([]);
  const [billToAddressList, setBillToAddressList] = useState([]);
  const [paymentMethodList, setPaymentMethodList] = useState([]);
  const [paymentTermInfo, setPaymentTermInfo] = useState(null);

  const [selectedShipToAddressId, setSelectedShipToAddressId] = useState();
  const [selectedBillToAddressId, setSelectedBillToAddressId] = useState();
  const [selectedPaymentId, setSelectedPaymentId] = useState();

  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] =
    useState(false);
  const [showAddBillAddressModal, setShowAddBillAddressModal] = useState(false);

  const paymentMethodOptions =
    paymentTermInfo?.paymentTermCode && paymentTermInfo?.paymentTermName
      ? [
          {
            idCC: PAYMENT_TERM_OPTION_ID,
            label: paymentTermInfo.paymentTermName,
            isPaymentTerm: true,
            paymentTermCode: paymentTermInfo.paymentTermCode,
          },
          ...paymentMethodList,
        ]
      : paymentMethodList;

  useEffect(() => {
    getDbAddressList();
    getDbPaymentMethodList();
    getDbPaymentTerm();
  }, [userInfo]);

  const getDbAddressList = async () => {
    // Get shipping addresses
    const shipResult = await getAddressList(userInfo.idCustomer, 'ship');
    setShipToAddressList(shipResult.address_list);
    if (shipResult.address_list.length > 0) {
      setSelectedShipToAddressId(shipResult.address_list[0].idAddr);
    }

    // Get billing addresses
    const billResult = await getAddressList(userInfo.idCustomer, 'bill');
    setBillToAddressList(billResult.address_list);
    if (billResult.address_list.length > 0) {
      setSelectedBillToAddressId(billResult.address_list[0].idAddr);
    }
  };

  const getDbPaymentMethodList = async () => {
    const result = await getPaymentMethodList(userInfo.idCustomer);
    const pmList = [
      {
        a1: '',
        a2: 'Credit Card',
        a3: '',
        a4: '',
        a5: 'cash',
        idCC: -1,
      },
    ];
    const finalList = [...pmList, ...result.payment_method_list];
    setPaymentMethodList(finalList);
  };

  const getDbPaymentTerm = async () => {
    try {
      const result = await getPaymentTerm(userInfo.idCustomer);
      setPaymentTermInfo(result.payment_term || null);
    } catch (error) {
      console.error('Error fetching payment term:', error);
    }
  };

  useEffect(() => {
    if (paymentTermInfo?.paymentTermCode && paymentTermInfo?.paymentTermName) {
      setSelectedPaymentId(PAYMENT_TERM_OPTION_ID);
      return;
    }

    if (paymentMethodList.length > 0) {
      setSelectedPaymentId((prev) =>
        prev !== undefined ? prev : paymentMethodList[0].idCC,
      );
    }
  }, [paymentMethodList, paymentTermInfo]);

  const handlePaymentNext = async () => {
    const trimmedPoNo = poNo.trim();

    if (!selectedShipToAddressId) {
      alert('You must select Shipping Address!');
      return;
    }
    if (!selectedBillToAddressId) {
      alert('You must select Billing Address!');
      return;
    }
    if (!selectedPaymentId) {
      alert('You must select Payment Method!');
      return;
    }

    if (trimmedPoNo.length > 0) {
      dispatch(setShowLoading({ showLoading: true }));
      const result = await checkSOExists(trimmedPoNo);
      dispatch(setShowLoading({ showLoading: false }));
      if (result.is_exist) {
        alert(`Customer PO# ${trimmedPoNo} is already exist!`);
        return;
      }
    }

    setPoNo(trimmedPoNo);
    setShipToInfo(
      shipToAddressList.find((l) => l.idAddr === selectedShipToAddressId),
    );
    setPaymentInfo(
      paymentMethodOptions.find((l) => l.idCC === selectedPaymentId),
    );
    setBillToInfo(
      billToAddressList.find((l) => l.idAddr === selectedBillToAddressId),
    );
    setCurrentStep(2);
  };

  return (
    <MainContainer>
      <LeftContainer>
        <Column style={{ marginBottom: '2rem', gap: '1rem' }}>
          <BodyText>Purchase Order Number(Optional)*</BodyText>
          <InputContainer>
            <Input
              type='text'
              value={poNo}
              onChange={(value) => setPoNo(value)}
            />
          </InputContainer>
        </Column>
        <PaymentInputView
          shipToAddressList={shipToAddressList}
          selectedShipToAddressId={selectedShipToAddressId}
          onSelectShipToAddress={(id) => setSelectedShipToAddressId(id)}
          billToAddressList={billToAddressList}
          selectedBillToAddressId={selectedBillToAddressId}
          onSelectBillToAddress={(id) => setSelectedBillToAddressId(id)}
          paymentMethodList={paymentMethodOptions}
          selectedPaymentId={selectedPaymentId}
          onSelectPayment={(id) => setSelectedPaymentId(id)}
          onAddShipToAddress={() => {
            setShowAddAddressModal(true);
          }}
          onAddBillToAddress={() => {
            setShowAddBillAddressModal(true);
          }}
          onAddPayment={() => {
            setShowAddPaymentMethodModal(true);
          }}
        />
        {/* Debug info removed */}
        <Subtitle>
          Payment terms will be applied when order is ready to ship.
        </Subtitle>
        {/* </Column> */}
      </LeftContainer>
      <Column style={{ flex: '0.6', gap: '1.5rem' }}>
        <RightContainer>
          <CartListConatiner>
            {cartList.map((cart) => {
              return <PaymentItemView key={cart.idCartDtl} item={cart} />;
            })}
          </CartListConatiner>
          <TotalBox>
            <Column>
              <Row style={{ justifyContent: 'space-between' }}>
                <Subtitle size='0.85rem'>Tax:</Subtitle>
                <Subtitle size='0.85rem'>{`$${parseFloat(totalTaxPrice).toFixed(2)}`}</Subtitle>
              </Row>
              <Row style={{ justifyContent: 'space-between' }}>
                <Subtitle size='0.85rem'>Shipping:</Subtitle>
                <Subtitle size='0.85rem'>{`$${parseFloat(totalShippingPrice).toFixed(2)}`}</Subtitle>
              </Row>
              <Row style={{ justifyContent: 'space-between' }}>
                <Subtitle
                  color={theme.colors.primaryText}
                  size='0.95rem'
                  weight='bold'
                >
                  Total:
                </Subtitle>
                <Subtitle
                  color={theme.colors.primaryText}
                  size='0.95rem'
                  weight='bold'
                >{`$${parseFloat(totalPrice).toFixed(2)}`}</Subtitle>
              </Row>
            </Column>
          </TotalBox>
        </RightContainer>
        <Button
          style={{
            width: '100%',
            opacity: 1,
          }}
          onClick={handlePaymentNext}
        >
          Next
        </Button>
      </Column>
      <AddAddressModal
        title={'Enter a New Shipping Address'}
        show={showAddAddressModal}
        refresh={getDbAddressList}
        onClose={() => {
          setShowAddAddressModal(false);
        }}
      />
      <AddPaymentMethodModal
        title={'Enter a New Shipping Address'}
        show={showAddPaymentMethodModal}
        refresh={getDbPaymentMethodList}
        onClose={() => {
          setShowAddPaymentMethodModal(false);
        }}
      />
      <AddAddressModal
        title={'Enter a New Billing Address'}
        show={showAddBillAddressModal}
        refresh={getDbAddressList}
        onClose={() => {
          setShowAddBillAddressModal(false);
        }}
      />
    </MainContainer>
  );
};

export default PaymentView;
